import { TranslocoService } from '@ngneat/transloco';
import { getLanguageValue, Sort } from '@utility';
import { Competitor } from './competitor';
import { RaceCommercialItem } from './race-commercial-item';
import { RaceAdditionalPricingItem } from './race-additional-pricing-item';
import { ApplicationFormSettings } from './race-registration-form-settings';
import { FieldConfig, FieldConfigType } from './field-config';
import { RaceSettings } from './race-settings';
import { Race } from './race';
import { RaceRegistration } from './race-registration/race-registration';
import { Country } from './country';
import { EventPaymentSettings } from './event-payment-settings';
import { TeamType } from './team-type';
import { CompetitorViewData } from './competitor-view-data';
import { CompetitorViewContext } from './competitor-view-context';
import { VisibilityTypes } from './visibility-types';
import { ApplicationFormFieldVisibilityResolver } from './application-form-field-visibility-resolver';
import {TeamRaceType} from "./team-race-type";

/**
 * Builds the read-only column/option data used to render the competitor table (see
 * `CompetitorViewDataAvailableFields` and `Option`). For building the *editable* registration
 * form, use `CompetitorFormHelper` instead.
 */
export class CompetitorViewHelper extends ApplicationFormFieldVisibilityResolver<keyof CompetitorViewData> {
    static readonly CUSTOM_FIELD_COLUMN_PREFIX = 'custom_field_';
    static readonly ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX = 'additional_pricing_item_';

    eventViewData?: CompetitorViewDataAvailableFields;
    readonly viewDataByRaceIds = new Map<number, CompetitorViewDataAvailableFields>();

    readonly competitorTableSelectedDefaultColumns = new Set<keyof CompetitorViewData>([
        'id', 'email', 'status', 'registrationId', 'startTime', 'raceName', 'startNumber', 'chipNumber', 'startNumberExtension', 'fullName', 'teamName', 'startBlock', 'sex', 'paymentStatus',
    ]);

    private readonly countryById = new Map<number, Country>();
    private readonly raceById = new Map<number, Race>();

    private readonly activeLang: string;
    private readonly customFieldConfigByRaceId = new Map<number, Map<number, FieldConfig>>();
    private readonly fieldOptionLabelByFieldId = new Map<number, Map<number, string>>();

    constructor(
        private readonly eventId: number,
        private readonly translocoService: TranslocoService,
        public readonly countries: Country[] = [],
        public readonly applicationFormSettingsMap = new Map<number, ApplicationFormSettings>(),
        public readonly paymentSettings?: EventPaymentSettings,
        public readonly raceSettingsMap = new Map<number, RaceSettings>(),
        public readonly commercialItemsMap = new Map<number, RaceCommercialItem[]>(),
        public readonly additionalPricingItemsMap = new Map<number, RaceAdditionalPricingItem[]>(),
        public readonly viewContext: CompetitorViewContext = CompetitorViewContext.PUBLIC,
        public readonly isMeasurement = false,
        public readonly races = new Map<number, Race>(),
        public readonly activeRace?: Race | undefined
    ) {
        super(viewContext);
        this.activeLang = this.translocoService.getActiveLang();

        for (const c of countries) if (c.id != null) this.countryById.set(c.id, c);
        for (const r of races.values()) if (r.id != null) this.raceById.set(r.id, r);

        this.buildCaches();
        this.init();
    }

    protected get visibilityFieldMap() {
        return ApplicationFormVisibleCompetitorFields;
    }

    protected get displayFlagFieldMap() {
        return ApplicationFormDisplayCompetitorFields;
    }

    private buildCaches(): void {
        for (const [raceId, appSettings] of this.applicationFormSettingsMap.entries()) {
            const visibleFields = this.getCustomFields(appSettings);
            const configById = new Map<number, FieldConfig>();
            for (const field of visibleFields) {
                if (!field.id) continue;
                configById.set(field.id, field);

                const labelsByOptionId = new Map<number, string>();
                for (const opt of field.options ?? []) {
                    if (!opt.id) continue;
                    labelsByOptionId.set(opt.id, this.getMultilangLabel(opt));
                }
                this.fieldOptionLabelByFieldId.set(field.id, labelsByOptionId);
            }

            this.customFieldConfigByRaceId.set(raceId, configById);
        }
    }

    private init(): void {
        for (const race of this.races.values()) {
            const raceId = race.id;
            if (!raceId) continue;
            this.viewDataByRaceIds.set(raceId, this.buildRaceView(raceId));
        }
        this.buildEventViewDataFromRaces();
    }

    private buildRaceView(raceId: number): CompetitorViewDataAvailableFields {
        const raceSettings = this.raceSettingsMap.get(raceId);
        const appSettings = this.applicationFormSettingsMap.get(raceId);
        const additionalPricing = this.additionalPricingItemsMap.get(raceId) ?? [];
        const isTeamRace = RaceSettings.isTeamRace(raceSettings);

        const view = this.buildViewData({
            raceSettings,
            applicationFormSettings: appSettings,
            raceAdditionalPricingItems: additionalPricing,
            isTeamRace,
            forSingleCompetitor: !isTeamRace,
        });

        // !raceSettings condition for legacy races
        if (isTeamRace || !raceSettings) {
            view.teamMemberView = this.buildViewData({
                raceSettings,
                applicationFormSettings: appSettings,
                raceAdditionalPricingItems: additionalPricing,
                isTeamRace: true,
                forSingleCompetitor: true,
            });
            view.teamMemberView.options = this.getOptions(view.teamMemberView, true);
        }

        view.options = this.getOptions(view, true);
        return view;
    }

    private buildEventViewDataFromRaces(): void {
        const merged = new CompetitorViewDataAvailableFields();

        for (const fields of this.viewDataByRaceIds.values()) {
            for (const k of fields.general) merged.general.add(k);
        }

        merged.general.add('raceName');
        merged.general = this.sortedSet(merged.general);
        merged.options = this.getOptions(merged, false);
        this.eventViewData = merged;
    }

    private buildViewData(args: {
        raceSettings: RaceSettings | undefined;
        applicationFormSettings: ApplicationFormSettings | undefined;
        raceAdditionalPricingItems: RaceAdditionalPricingItem[];
        forSingleCompetitor: boolean;
        isTeamRace: boolean;
    }): CompetitorViewDataAvailableFields {
        const { raceSettings, applicationFormSettings, raceAdditionalPricingItems, forSingleCompetitor, isTeamRace } =
            args;

        const general = new Set<keyof CompetitorViewData>();
        let custom: FieldConfig[] = [];
        let pricing: RaceAdditionalPricingItem[] = [];

        const availableFields = this.getCompetitorTableAvailableColumnsBasedOnApplicationFormSettings(
            applicationFormSettings,
            raceSettings,
            forSingleCompetitor,
            isTeamRace
        );
        for (const [key, isAvailable] of availableFields) {
            if (!isAvailable) continue;

            if (key === 'customFields' && applicationFormSettings) {
                custom = this.getCustomFields(applicationFormSettings);
            } else if (key === 'raceAdditionalItems') {
                pricing = this.getCompetitorTablePriceFields(raceAdditionalPricingItems);
            } else {
                general.add(key);
            }
        }

        const viewData = new CompetitorViewDataAvailableFields();
        viewData.general = this.sortedSet(general);
        viewData.custom = custom;
        viewData.pricing = pricing;
        return viewData;
    }

    private sortedSet(set: Set<keyof CompetitorViewData>): Set<keyof CompetitorViewData> {
        const arr = [...set];
        Sort.sortByOrder(arr, [...CompetitorTableRowDataSortingArray]);
        return new Set(arr);
    }

    public competitorToCompetitorViewData(competitor: Competitor, isParentCompetitor: boolean = true): CompetitorViewData {
        const row: CompetitorViewData = { ...(competitor as CompetitorViewData) };
        row.paymentStatus = RaceRegistration.getAmountStatus(competitor.registration);

        const raceId = competitor.raceId ?? this.activeRace?.id;
        const raceSettings = raceId ? this.raceSettingsMap.get(raceId) : undefined;
        const isConcurrentRace = RaceSettings.isConcurrentRace(raceSettings);

        if (!raceSettings) {
            row.fullName = competitor.fullName;
        } else {
            const isTeamRace = RaceSettings.isTeamRace(raceSettings);
            row.fullName = !(isTeamRace && isParentCompetitor) ? `${competitor?.firstName ?? ''} ${competitor?.lastName ?? ''}`.trim() : undefined;
            row.teamName = isTeamRace && isParentCompetitor ? `${competitor?.lastName ?? ''}`.trim() : undefined;
        }

        if (competitor.country) {
            const country = this.countryById.get(competitor.country);
            row.country = country?.id ? this.translocoService.translate(`app.common.country.${country.id}`) : '-';
            row.countryShort = country?.iocCode ?? '-';
        }
        if (competitor.nationality) {
            const country = this.countryById.get(competitor.nationality);
            row.nationality = country?.id ? this.translocoService.translate(`app.common.country.${country.id}`) : '-';
        }

        if (raceId) {
            row.customFields = this.transformCustomFieldValues(competitor, raceId);
            row.raceAdditionalItems = this.transformAdditionalPricingItems(competitor, this.additionalPricingItemsMap.get(raceId) ?? []);

            const race = this.raceById.get(raceId);
            row.raceName = getLanguageValue(race?.name, race?.nameEng, race?.nameIta, race?.nameSlo, race?.nameSr, race?.nameBs, this.activeLang, race?.name);
        }

        if (competitor.raceCommercialItem) {
            const i = competitor.raceCommercialItem;
            row.raceCommercialItemValue = getLanguageValue(i.name, i.nameEng, i.nameIta, i.nameSlo, i.nameSr, i.nameBs, this.activeLang, i.name);
        }

        row.fee = competitor.fee;
        row.tshirt = competitor.tshirt ? this.translocoService.translate(`app.common.tshirt.${competitor.tshirt}`) : undefined;
        row.meal = competitor.meal ? this.translocoService.translate(`app.common.meal.${competitor.meal}`) : undefined;
        row.transport = competitor.transport ? this.translocoService.translate(`app.common.transport.${competitor.transport}`) : undefined;
        row.discipline = competitor.discipline
            ? this.translocoService.translate(`race.registration.settings.discipline.${competitor.discipline}`)
            : undefined;

        if (competitor.teamMembers?.length) {
            row.teamMembers = competitor.teamMembers.map((m) => this.competitorToCompetitorViewData(m, false));

            if (isConcurrentRace && isParentCompetitor && !row.startTime) {
                row.startTime = this.getLatestTeamMemberStartTime(competitor.teamMembers);
            }
        }
        return row;
    }

    private getLatestTeamMemberStartTime(teamMembers: Competitor[]): Date | undefined {
        const times: Date[] = [];

        for (const member of teamMembers) {
            if (member.startTime) {
                times.push(new Date(member.startTime));
            }
        }

        if (!times.length) return undefined;
        return new Date(Math.min(...times.map((t) => t.getTime())));
    }

    getCompetitorTablePriceFields(additionalPricingItems: RaceAdditionalPricingItem[]): RaceAdditionalPricingItem[] {
        return (additionalPricingItems ?? []).filter((item) => item.fieldVisibility !== VisibilityTypes.NOT_VISIBLE);
    }

    getCompetitorTableAvailableColumnsBasedOnApplicationFormSettings(applicationFormSettings: ApplicationFormSettings | undefined, raceSettings: RaceSettings | undefined,
                                                                     forSingleCompetitor: boolean, isTeamRace: boolean): Map<keyof CompetitorViewData, boolean> {
        const isAdmin = this.isAdmin();
        const isSelf = this.isSelf();
        const show = (field: keyof CompetitorViewData) => forSingleCompetitor && this.shouldShowField(field, applicationFormSettings);
        const forTeamOrSingle = (isTeamRace && !forSingleCompetitor) || !isTeamRace;
        const isConcurrentRace : boolean = RaceSettings.isConcurrentRace(raceSettings);
        return new Map<keyof CompetitorViewData, boolean>([
            ['id', isAdmin],
            ['email', isAdmin || (isSelf && forSingleCompetitor)],
            ['created', isAdmin],
            ['modified', isAdmin],
            ['registrationId', isAdmin && forTeamOrSingle],
            ['status', isAdmin && forTeamOrSingle],
            ['fullName', forSingleCompetitor],
            ['teamName', isTeamRace && !forSingleCompetitor],
            ['sex', show('sex')],
            ['country', show('country')],
            ['categoryLabel', show('categoryLabel')],
            ['club', show('club')],
            ['startNumber', forTeamOrSingle],
            ['startBlock', !!(forTeamOrSingle && raceSettings?.blockStart)],
            ['startNumberExtension', (forSingleCompetitor && raceSettings?.teamRaceType === TeamRaceType.CONCURRENT)],
            ['nickname', show('nickname')],
            ['mobilePhone', show('mobilePhone')],
            ['placeOfResidence', show('placeOfResidence')],
            ['address', show('address')],
            ['identityCardNumber', show('identityCardNumber')],
            ['identityCardExpires', show('identityCardExpires')],
            ['identityCardIssuer', show('identityCardIssuer')],
            ['tshirt', show('tshirt')],
            ['transport', show('transport')],
            ['meal', show('meal')],
            ['licence', show('licence')],
            ['emergencyPhone', show('emergencyPhone')],
            ['birthday', (isAdmin || isSelf) && show('birthday')],
            ['nationality', show('nationality')],
            ['discipline', forSingleCompetitor && isTeamRace && this.shouldShowField('discipline', applicationFormSettings)],
            ['chipNumber', isAdmin && this.shouldShowField('chipNumber', applicationFormSettings)],
            ['bestTime', show('bestTime')],
            ['fee', isAdmin && forTeamOrSingle],
            ['paymentStatus', (isAdmin || !!this.paymentSettings?.displayPaymentStatusForRegistrants) && forTeamOrSingle],
            ['startTime', isAdmin && this.isMeasurement && (forTeamOrSingle || isConcurrentRace)],
            ['customFields', forSingleCompetitor && !!applicationFormSettings?.customFields?.length],
            ['raceAdditionalItems', isAdmin && forSingleCompetitor],
        ]);
    }

    getCustomFields(applicationFormSettings: ApplicationFormSettings): FieldConfig[] {
        return (applicationFormSettings.customFields ?? []).filter((f) => this.isCustomFieldVisible(f));
    }

    getOptions(viewData: CompetitorViewDataAvailableFields, defaultSelection: boolean): Map<string, Option> {
        const optionsMap = new Map<string, Option>();

        for (const option of viewData.general) {
            const selected = this.competitorTableSelectedDefaultColumns.has(option) || defaultSelection;
            optionsMap.set(
                option,
                new Option(option, this.translocoService.translate(`competitor.table.${option}`), selected, true, false, false)
            );
        }

        this.addOptionsToMap(viewData.custom, CompetitorViewHelper.CUSTOM_FIELD_COLUMN_PREFIX, optionsMap, defaultSelection, true, false);
        this.addOptionsToMap(
            viewData.pricing,
            CompetitorViewHelper.ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX,
            optionsMap,
            defaultSelection,
            false,
            true
        );
        return optionsMap;
    }

    selectDefault(viewData: CompetitorViewDataAvailableFields): void {
        const defaultSelection = new Set<string>([...this.competitorTableSelectedDefaultColumns].map(String));
        viewData.options.forEach((option) => (option.selected = defaultSelection.has(option.key)));
    }

    private addOptionsToMap(
        options: (FieldConfig | RaceAdditionalPricingItem)[],
        prefix: string,
        optionsMap: Map<string, Option>,
        defaultSelection: boolean,
        isCustom: boolean,
        isPricing: boolean
    ): void {
        for (const opt of options) {
            if (!opt.id) continue;

            const label = this.getMultilangLabel(opt);
            const key = CompetitorViewHelper.getColumnId(opt.id, prefix);
            optionsMap.set(key, new Option(key, label, defaultSelection, false, isCustom, isPricing));
        }
    }

    private getMultilangLabel(entity: {
        name?: string;
        nameEng?: string;
        nameIta?: string;
        nameSlo?: string;
        nameSr?: string;
        nameBs?: string;
    }): string {
        return (
            getLanguageValue(
                entity.name,
                entity.nameEng,
                entity.nameIta,
                entity.nameSlo,
                entity.nameSr,
                entity.nameBs,
                this.activeLang,
                entity.name ?? entity.nameEng ?? entity.nameIta ?? entity.nameSlo ?? entity.nameSr ?? entity.nameBs
            ) ?? '-'
        );
    }

    static getColumnId(id: number, prefix: string): string {
        return `${prefix}${id}`;
    }

    transformCustomFieldValues(competitor: Competitor, raceId: number): Map<string, string> {
        const valueMap = new Map<string, string>();
        const configById = this.customFieldConfigByRaceId.get(raceId);
        if (!configById) return valueMap;

        for (const value of competitor.customFieldValues ?? []) {
            if (!value.fieldId) continue;

            const key = CompetitorViewHelper.getColumnId(value.fieldId, CompetitorViewHelper.CUSTOM_FIELD_COLUMN_PREFIX);
            const fieldConfig = configById.get(value.fieldId);

            if (!fieldConfig) {
                valueMap.set(key, '-');
                continue;
            }

            let fieldValue: string | undefined;

            switch (fieldConfig.fieldConfigTypeId) {
                case FieldConfigType.BOOLEAN:
                    if (value.boolValue === true) fieldValue = this.translocoService.translate('app.common.yes');
                    else if (value.boolValue === false) fieldValue = this.translocoService.translate('app.common.no');
                    break;

                case FieldConfigType.SELECT:
                case FieldConfigType.MULTISELECT: {
                    const labelsByOptionId = this.fieldOptionLabelByFieldId.get(fieldConfig.id ?? -1);
                    fieldValue = (value.numberList ?? [])
                        .map((selection: number) => labelsByOptionId?.get(selection))
                        .filter((x): x is string => !!x)
                        .join(', ');
                    break;
                }

                case FieldConfigType.TEXT:
                    fieldValue = value.inputString;
                    break;

                case FieldConfigType.NUMBER:
                    fieldValue = value.numberValue?.toString();
                    break;

                case FieldConfigType.DATE:
                    // todo: localize date
                    break;

                case FieldConfigType.TIME:
                    // todo: localize time
                    break;

                default:
                    break;
            }

            valueMap.set(key, fieldValue?.length ? fieldValue : '-');
        }

        return valueMap;
    }

    public selectFields(cols: (string | number)[], viewData: CompetitorViewDataAvailableFields): void {
        const setDefault = !cols?.length;
        if (setDefault) {
            this.selectDefault(viewData);
            return;
        }

        const selected = new Set(cols.map(String));
        viewData.options.forEach((option) => (option.selected = selected.has(option.key)));
    }

    /**
     * Selects every available column on `viewData` except the given keys.
     * Useful when a view wants "everything by default" instead of maintaining
     * an explicit allow-list.
     */
    public selectAllExcept(
        viewData: CompetitorViewDataAvailableFields,
        ignoredKeys: Iterable<keyof CompetitorViewData | string> = []
    ): void {
        const ignored = new Set([...ignoredKeys].map(String));
        const selectableKeys = [...viewData.options.keys()].filter((key) => !ignored.has(key));
        this.selectFields(selectableKeys, viewData);
    }

    transformAdditionalPricingItems(competitor: Competitor, additionalPricingItems: RaceAdditionalPricingItem[]): Map<string, string> {
        const valueMap = new Map<string, string>();
        if (!additionalPricingItems?.length) return valueMap;

        for (const value of competitor.raceAdditionalPricingItems ?? []) {
            if (!value.id) continue;

            const key = CompetitorViewHelper.getColumnId(
                value.id,
                CompetitorViewHelper.ADDITIONAL_PRICING_ITEM_COLUMN_PREFIX
            );
            const opt = value.pricingItemValues?.at(0);

            if (opt) {
                valueMap.set(key, this.getMultilangLabel(opt));
            } else {
                valueMap.set(key, this.translocoService.translate('app.common.yes'));
            }
        }

        return valueMap;
    }

    private static readonly VIEW_FIELD_TO_DTO_FIELD: Partial<Record<keyof CompetitorViewData, (keyof Competitor)[]>> = {
        id: ['id'],
        registrationId: ['registrationId'],
        startBlock: ['startBlock'],
        startNumber: ['startNumber'],
        fullName: ['firstName', 'lastName'],
        teamName: ['lastName'],
        nickname: ['nickname'],
        email: ['email'],
        startNumberExtension: ['startNumberExtension'],
        status: ['status'],
        sex: ['sex'],
        mobilePhone: ['mobilePhone'],
        raceId: ['raceId'],
        raceName: ['raceId'],
        paymentStatus: ['registration'],
        country: ['country'],
        countryShort: ['country'],
        categoryLabel: ['categoryLabel'],
        placeOfResidence: ['placeOfResidence'],
        address: ['address'],
        identityCardNumber: ['identityCardNumber'],
        identityCardExpires: ['identityCardExpires'],
        identityCardIssuer: ['identityCardIssuer'],
        club: ['club'],
        tshirt: ['tshirt'],
        meal: ['meal'],
        transport: ['transport'],
        comment: ['comment'],
        chipNumber: ['chipNumber'],
        licence: ['licence'],
        emergencyPhone: ['emergencyPhone'],
        fee: ['fee'],
        discipline: ['discipline'],
        nationality: ['nationality'],
        bestTime: ['bestTime'],
        controlPointsTimes: ['controlPointsTimes'],
        startTime: ['startTime'],
        birthday: ['birthday'],
    };
}

export class CompetitorViewDataAvailableFields {
    general: Set<keyof CompetitorViewData> = new Set<keyof CompetitorViewData>();
    generalCompetitorFields: Set<keyof Competitor> = new Set<keyof Competitor>();
    custom: FieldConfig[] = [];
    pricing: RaceAdditionalPricingItem[] = [];
    options: Map<string, Option> = new Map<string, Option>();
    teamMemberView?: CompetitorViewDataAvailableFields;
}

export class Option {
    constructor(
        public key: string = '',
        public label: string = '',
        public selected: boolean = false,
        public isGeneral: boolean = false,
        public isCustom: boolean = false,
        public isPricing: boolean = false
    ) {}
}

export const CompetitorTableRowDataSortingArray: (keyof CompetitorViewData)[] = [
    'id',
    'registrationId',
    'startBlock',
    'startNumber',
    'fullName',
    'teamName',
    'chipNumber',
    'startNumberExtension',
    'email',
    'status',
    'sex',
    'startTime',
    'paymentStatus',
    'raceName',
    'birthday',
    'categoryLabel',
    'country',
    'placeOfResidence',
    'address',
    'mobilePhone',
    'club',
    'identityCardNumber',
    'identityCardExpires',
    'identityCardIssuer',
    'tshirt',
    'meal',
    'transport',
    'comment',
    'licence',
    'emergencyPhone',
    'fee',
    'discipline',
    'nationality',
    'controlPointsTimes',
    'created',
    'modified',
];

export const ApplicationFormVisibleCompetitorFields: Map<keyof CompetitorViewData, keyof ApplicationFormSettings> = new Map([
    ['sex', 'genderVisibility'],
    ['country', 'countryVisibility'],
    ['categoryLabel', 'categoryRegistrationVisibility'],
    ['placeOfResidence', 'cityVisibility'],
    ['address', 'addressVisibility'],
    ['identityCardNumber', 'identityCardNumberVisibility'],
    ['identityCardExpires', 'identityCardExpiresVisibility'],
    ['identityCardIssuer', 'identityCardIssuerVisibility'],
    ['club', 'clubVisibility'],
    ['mobilePhone', 'mobilePhoneVisibility'],
    ['tshirt', 'tshirtVisibility'],
    ['meal', 'mealVisibility'],
    ['transport', 'transportVisibility'],
    ['comment', 'commentVisibility'],
    ['chipNumber', 'chipNumberVisibility'],
    ['bestTime', 'bestTimeVisibility'],
    ['licence', 'licenceVisibility'],
    ['emergencyPhone', 'emergencyPhoneVisibility'],
    ['fee', 'feeVisibility'],
    ['birthday', 'birthdayRegistrationDisplayFlag'],
    ['discipline', 'disciplineVisibility'],
    ['nationality', 'nationalityVisibility'],
]);

export const ApplicationFormDisplayCompetitorFields: Map<
    keyof CompetitorViewData,
    keyof ApplicationFormSettings
    > = new Map([
    ['sex', 'genderRegistrationDisplayFlag'],
    ['country', 'countryRegistrationDisplayFlag'],
    ['categoryLabel', 'categoryRegistrationVisibility'],
    ['placeOfResidence', 'cityRegistrationDisplayFlag'],
    ['address', 'addressRegistrationDisplayFlag'],
    ['identityCardNumber', 'identityCardNumberRegistrationDisplayFlag'],
    ['identityCardExpires', 'identityCardExpiresRegistrationDisplayFlag'],
    ['identityCardIssuer', 'identityCardIssuerRegistrationDisplayFlag'],
    ['club', 'clubRegistrationDisplayFlag'],
    ['mobilePhone', 'mobilePhoneRegistrationDisplayFlag'],
    ['tshirt', 'tshirtRegistrationDisplayFlag'],
    ['meal', 'mealRegistrationDisplayFlag'],
    ['transport', 'transportRegistrationDisplayFlag'],
    ['comment', 'commentRegistrationDisplayFlag'],
    ['chipNumber', 'chipNumberRegistrationDisplayFlag'],
    ['bestTime', 'bestTimeRegistrationDisplayFlag'],
    ['licence', 'licenceRegistrationDisplayFlag'],
    ['emergencyPhone', 'emergencyPhoneRegistrationDisplayFlag'],
    ['fee', 'feeVisibility'],
    ['birthday', 'birthdayRegistrationDisplayFlag'],
    ['discipline', 'disciplineVisibility'],
    ['nationality', 'nationalityRegistrationDisplayFlag'],
]);